import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo } from '@/mocks/videos';

export default function AboutPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91视频',
    description: aboutInfo.description,
    url: 'https://www.91video.com/about',
    about: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      foundingDate: '2018',
      description: aboutInfo.description,
      contactPoint: {
        '@type': 'ContactPoint',
        email: aboutInfo.contact.email,
        telephone: `+86-${aboutInfo.contact.phone}`,
        contactType: 'customer service',
      },
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <Navbar />
      <main className="min-h-screen pt-20 pb-12">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-12 md:mb-16">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
                关于{aboutInfo.brandName}
              </h1>
              <p className="text-foreground-500 text-sm max-w-3xl mx-auto">
                成立于{aboutInfo.founded}
              </p>
            </div>

            <div className="relative w-full h-[280px] md:h-[400px] rounded-2xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20entertainment%20media%20company%20office%20interior%20with%20warm%20lighting%2C%20large%20screens%20displaying%20movie%20posters%2C%20creative%20workspace%20with%20comfortable%20seating%2C%20professional%20video%20production%20environment%2C%20golden%20hour%20light%20streaming%20through%20windows%2C%20warm%20coral%20and%20cream%20color%20palette&width=1600&height=800&seq=about-hero-01&orientation=landscape"
                alt="91视频办公环境"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-black/50"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white px-4">
                  <h2 className="text-2xl md:text-3xl font-bold font-heading mb-3">让优质内容触手可及</h2>
                  <p className="text-white/80 text-sm max-w-xl mx-auto">我们致力于打造全球华人最喜爱的在线视频平台</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="p-6 rounded-lg bg-white border border-background-200">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-eye-line text-primary-500"></i>
                  </span>
                  我们的愿景
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
              </div>
              <div className="p-6 rounded-lg bg-white border border-background-200">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-flag-line text-primary-500"></i>
                  </span>
                  我们的使命
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
              </div>
            </div>

            <div className="p-6 rounded-lg bg-background-100">
              <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">品牌介绍</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.description}</p>
            </div>
          </section>

          <section className="mb-12 md:mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">为什么选择我们</h2>
              <p className="text-foreground-500 text-sm">六大核心优势，为您带来极致的观影体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {aboutInfo.advantages.map((adv, index) => (
                <div key={index} className="p-5 rounded-lg bg-white border border-background-200 transition-all hover:scale-[1.02]">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 mb-4">
                    <i className={`${adv.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-bold font-heading text-foreground-900 mb-2 text-sm">{adv.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{adv.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12 md:mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">联系我们</h2>
              <p className="text-foreground-500 text-sm">有任何问题或建议？我们随时为您服务</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 rounded-lg bg-white border border-background-200 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</h4>
                <p className="text-xs text-foreground-500">{aboutInfo.contact.email}</p>
              </div>
              <div className="p-5 rounded-lg bg-white border border-background-200 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className="ri-phone-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-medium text-foreground-900 text-sm mb-1">客服电话</h4>
                <p className="text-xs text-foreground-500">{aboutInfo.contact.phone}</p>
              </div>
              <div className="p-5 rounded-lg bg-white border border-background-200 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className="ri-map-pin-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-medium text-foreground-900 text-sm mb-1">公司地址</h4>
                <p className="text-xs text-foreground-500">{aboutInfo.contact.address}</p>
              </div>
              <div className="p-5 rounded-lg bg-white border border-background-200 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className="ri-wechat-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-medium text-foreground-900 text-sm mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-500">{aboutInfo.contact.wechat}</p>
              </div>
            </div>
          </section>

          <section className="mb-12 md:mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">常见问题</h2>
              <p className="text-foreground-500 text-sm">您关心的问题，这里都有答案</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutInfo.faqs.map((faq, index) => (
                <details key={index} className="group rounded-lg bg-white border border-background-200 overflow-hidden">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between list-none">
                    <h3 className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</h3>
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-foreground-400"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}